// ─── src/chat/WorkspaceRight.jsx ──────────────────────────────────────────────
import { useState, useEffect, useMemo } from "react";
import { useAura }         from "../stores/auraCore";
import { transformListing, assertCanonical, imgSrc } from "../utils/transformListing";
import { listingProfileUrl } from "../utils/listingUrl";
import { roleFromCategory } from "../services/weddingStore";
import { bestCandidate, reasonPhrase, countryOf } from "../services/weddingReasoning";
import { supabaseAnon } from "../lib/supabaseClient";
import RecommendationCard  from "./RecommendationCard";
import ArticlePreview      from "../components/chat/ArticlePreview";
import ProductPreview      from "../components/chat/ProductPreview";
import QuickViewModal      from "../components/modals/QuickViewModal";
import BulkEnquiryModal    from "./BulkEnquiryModal";
import { saveCompareList } from "../compare/GlobalCompare";
import { useCoupleAuth }   from "../context/CoupleAuthContext";
import { isPreviewPrivate } from "./previewMode";
import { timeHorizon, monthsUntil, describeTiming } from "../utils/weddingTiming";
import { buildVenueVideos, mapMediaItemToGalleryPhoto } from "../utils/mediaMappers";
import VideoPlayModal      from "../components/video/VideoPlayModal";
import PhotoLightbox       from "../components/profile/Lightbox";
import Icon                from "./Icons";

const GOLD = "#d39a21";

// ── Theme helper ───────────────────────────────────────────────────────────────
function getT(dark) {
  if (dark) return {
    bg:      "#0D0B09",
    text:    "#FFFFFF",
    grey:    "rgba(255,255,255,0.74)",
    divider: "rgba(211,154,33,0.16)",
    emptyBg: "none",
  };
  return {
    bg:      "#f3efeb",
    text:    "#1A1714",
    grey:    "rgba(26,23,20,0.75)",
    divider: "rgba(26,23,20,0.1)",
    emptyBg: "none",
  };
}

// ── Collapsed state ────────────────────────────────────────────────────────────
function CollapsedRight({ itemCount, darkMode }) {
  const T = getT(darkMode);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "20px 0", gap: 20, height: "100%" }}>
      <Icon name="sparkleMini" size={16} color={GOLD} style={{ opacity: 0.75 }} />
      {itemCount > 0 && (
        <div style={{
          background: GOLD, color: "#0D0B09",
          width: 26, height: 26, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 10, fontWeight: 800, fontFamily: "var(--font-body)",
        }}>
          {itemCount > 9 ? "9+" : itemCount}
        </div>
      )}
    </div>
  );
}

// ── Card skeleton, shown while live venue/vendor data is loading ──────────────
function CardSkeleton({ darkMode }) {
  const base  = darkMode ? "rgba(255,255,255,0.05)" : "rgba(26,23,20,0.07)";
  const shine = darkMode ? "rgba(255,255,255,0.09)" : "rgba(26,23,20,0.12)";
  const border = darkMode ? "rgba(255,255,255,0.07)" : "rgba(26,23,20,0.10)";
  return (
    <div style={{ borderRadius: "var(--lwd-radius-card)", overflow: "hidden", border: `1px solid ${border}`, marginBottom: 14 }}>
      <div style={{ height: 152, background: base, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(90deg, transparent 0%, ${shine} 50%, transparent 100%)`, animation: "ws-shimmer 1.6s ease infinite" }} />
      </div>
      <div style={{ padding: "11px 13px 13px", display: "flex", flexDirection: "column", gap: 7 }}>
        <div style={{ height: 14, width: "68%", borderRadius: 4, background: base }} />
        <div style={{ height: 9,  width: "42%", borderRadius: 4, background: base }} />
        <div style={{ height: 9,  width: "52%", borderRadius: 4, background: base, marginTop: 3 }} />
      </div>
    </div>
  );
}

// ── Your Wedding panel, the persistent Wedding entity, led at the top ───────
// Reads wedding + team from the store (populated by Aura's silent assembly).
// Renders nothing until a wedding is minted (confidence >= 60).
const WED_STATE = {
  needed:      { label: "Needed",      color: "rgba(255,255,255,0.4)", dark: "rgba(26,23,20,0.4)" },
  suggested:   { label: "Suggested",   color: "#c9a24b", dark: "#9a7b28" },
  exploring:   { label: "Exploring",   color: "#d39a21", dark: "#9a6f1a" },
  shortlisted: { label: "Shortlisted", color: "#e0b64d", dark: "#8B7432" },
  enquired:    { label: "Enquired",    color: "#10b981", dark: "#0e8f66" },
  booked:      { label: "Booked",      color: "#22c55e", dark: "#16a34a" },
};
const WED_ROLE = {
  venue: "Venue", planner: "Planner", photographer: "Photographer", videographer: "Videographer",
  florist: "Florist", catering: "Food & Drink", entertainment: "Entertainment",
  celebrant: "Celebrant", vendor: "Supplier",
};
// Wedding-logical order (not alphabetical), the roster should read the way a
// couple plans, venue first.
const WED_ROLE_ORDER = ["venue", "planner", "photographer", "videographer", "florist", "catering", "entertainment", "celebrant", "vendor"];
const roleRank = (r) => { const i = WED_ROLE_ORDER.indexOf(r); return i === -1 ? WED_ROLE_ORDER.length : i; };

const SETTLED_STATES = ["shortlisted", "enquired", "booked"];

const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short" }); } catch { return ""; } };
// Warm the evidence heading with the actual name, trimming a trailing generic
// supplier suffix ("Giuseppe Suma Weddings" → "Giuseppe Suma"); venue/business
// names are left intact.
const suggestedName = (name) => (name || "them").trim().replace(/\s+(weddings?|photography|films?|studios?)$/i, "");

// Wedding Health as GUIDANCE, never a score: one quiet concierge line naming the
// single next best action from the team's real states. Returns null when there's
// nothing meaningful to guide yet (the rail shows its resting line instead). Pure,
// reads state, deposits nothing (a deposit only happens when the couple acts).
// Aligned to the canonical WED_ROLE_ORDER, videographer + catering were missing,
// so guidance skipped a real gap (videography) and named a later one (floristry).
const GUIDE_PRIORITY = ["venue", "planner", "photographer", "videographer", "florist", "catering", "entertainment"];
const GUIDE_WORD = { venue: "venue", planner: "planner", photographer: "photographer", videographer: "videographer", florist: "florist", catering: "catering", entertainment: "entertainment" };
const GUIDE_A    = { venue: "a venue", planner: "a planner", photographer: "a photographer", videographer: "a videographer", florist: "a florist", catering: "catering", entertainment: "entertainment" };
const GUIDE_GAP  = {
  venue:        "I'd start with the venue, it anchors everything.",
  planner:      "Next, I'd choose a planner.",
  photographer: "Next, I'd look at photography.",
  videographer: "Next, I'd think about a videographer.",
  florist:      "The main gap now is floristry.",
  catering:     "Next, I'd settle the catering.",
  entertainment:"When you're ready, I'd think about entertainment.",
};
// Nicer nouns for the multi-gap line, so we never call one gap "the main" one
// while the couple is actively working another.
const GAP_NOUN = { venue: "the venue", planner: "a planner", photographer: "photography", videographer: "videography", florist: "floristry", catering: "catering", entertainment: "entertainment" };
// One quiet concierge line, the soul of Aura. Guidance, never a score. Grounded
// only in what the wedding actually holds (style + real team states). Present at
// every stage of the journey: a warm opener for a new wedding, the beloved
// mid-journey "coming together" lines, and a calm close when the team is complete.
function weddingGuidance(wedding, team) {
  if (!wedding) return null;
  const state = {};
  (team || []).forEach((t) => { if (t?.role) state[t.role] = t.state; });
  const secured   = (r) => SETTLED_STATES.includes(state[r]);
  const exploring = (r) => state[r] === "exploring" || state[r] === "suggested";
  const active    = (r) => secured(r) || exploring(r);

  const securedRoles   = GUIDE_PRIORITY.filter(secured);
  const exploringRoles = GUIDE_PRIORITY.filter((r) => exploring(r) && !secured(r));

  // The heartbeat: how far away the day is changes what Aura steers toward.
  const horizon = timeHorizon(wedding.wedding_date ? monthsUntil(wedding.wedding_date) : null);

  // Early state, nothing chosen yet. Name the style if we know it, then the first
  // real step, shaped by time. The voice is present from the very first moment.
  if (!securedRoles.length && !exploringRoles.length) {
    const flavour = wedding.style ? `Your ${String(wedding.style).toLowerCase()} wedding is taking shape.` : "Your wedding is taking shape.";
    const venueStep = horizon === "imminent"
      ? "Your date is close, so let's focus on venues still open for it."
      : horizon === "soon"
        ? "With only a few months, I'd lock the venue quickly."
        : "I'd start with the venue, it anchors everything.";
    return `${flavour} ${venueStep}`;
  }

  // Planner wisdom: a venue is chosen but no planner in play, steer there first.
  if (secured("venue") && !active("planner")) {
    return "You've shortlisted a venue. I'd suggest choosing a planner before adding more suppliers.";
  }

  const gaps = GUIDE_PRIORITY.filter((r) => !active(r));

  let ack;
  if (securedRoles.length >= 3)       ack = "Your wedding is really coming together.";
  else if (securedRoles.length === 2) ack = `Your ${GUIDE_WORD[securedRoles[0]]} and ${GUIDE_WORD[securedRoles[1]]} are coming together.`;
  else if (securedRoles.length === 1) ack = `You've shortlisted ${GUIDE_A[securedRoles[0]]}.`;
  else                                ack = "Your wedding is taking shape.";

  if (!gaps.length) return `${ack} Your team is complete. Reach out to anyone whenever you're ready.`;
  if (gaps.length === 1) return `${ack} ${GUIDE_GAP[gaps[0]]}`;
  // Several still open, name the next two in planning order rather than declaring
  // one "main" gap while the couple may be working another.
  const twoList = gaps.slice(0, 2).map((r) => GAP_NOUN[r]).join(" and ");
  return `${ack} ${twoList.charAt(0).toUpperCase() + twoList.slice(1)} are still to sort.`;
}

// A state badge, shared across slots.
function StateBadge({ state, darkMode }) {
  const st = WED_STATE[state] || WED_STATE.needed;
  const c = darkMode ? st.color : st.dark;
  return (
    <span style={{
      fontFamily: "var(--font-body)", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
      textTransform: "uppercase", color: c, border: `1px solid ${c}55`,
      padding: "3px 8px", borderRadius: "var(--lwd-radius-input)", whiteSpace: "nowrap", flexShrink: 0,
    }}>
      {st.label}
    </span>
  );
}

// GROUNDED reasons only, the couple-readable match_reasons Aura confirmed, plus
// an objective editorial signal. No generic filler; if nothing is provable, the
// "why" section simply doesn't render.
function capReasonPhrase(c) {
  const s = String(c || "").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : null;
}
function groundedReasons(item) {
  const out = [];
  const caps = Array.isArray(item.matchReasons) ? item.matchReasons : [];
  for (const c of caps) { if (out.length >= 3) break; const p = capReasonPhrase(c); if (p) out.push(p); }
  if (out.length < 3 && (item.scores?.editorialQuality ?? 0) >= 60) out.push("Featured in the LWD Edit");
  return out.slice(0, 3);
}

function candidateUrl(item) {
  return listingProfileUrl(item) || '#';
}

// Checklist marker for a role, ✓ when the role holds someone, ○ when it's still
// open. Makes the wedding read as a composition at a glance.
function RoleMark({ filled }) {
  return <span aria-hidden="true" style={{ marginRight: 8, fontSize: 10.5, color: filled ? GOLD : "currentColor", opacity: filled ? 1 : 0.75 }}>{filled ? "✓" : "○"}</span>;
}

// A filled slot, an entity the wedding already holds (compact, permanent).
// Clicking it FOCUSES the entity: the right column recomposes into its dossier
// (Entity Focus). The "why chosen" evidence now lives there, not inline.
function FilledSlot({ role, entity, state, image, darkMode, onFocus, active }) {
  const textColor = darkMode ? "#f5f0e8" : "#1A1714";
  const subColor  = darkMode ? "rgba(245,240,232,0.4)" : "rgba(26,23,20,0.42)";
  const isVenue = role === "venue";
  // Imagery is a reward for JOINING the team, a small landscape for a place,
  // a tiny portrait for a person. Never large (that would be a directory card).
  const thumb = image ? (
    <div aria-hidden="true" style={{
      width: isVenue ? 46 : 32, height: isVenue ? 34 : 32,
      borderRadius: isVenue ? 6 : "50%", flexShrink: 0,
      backgroundImage: `url(${imgSrc(image)})`, backgroundSize: "cover", backgroundPosition: "center",
      border: "1px solid rgba(211,154,33,0.28)",
    }} />
  ) : null;
  // Selected → the row LIFTS like paper: a touch lighter, a soft border, a whisper
  // of shadow. The component itself says "you're here", not a browser focus ring.
  return (
    <div
      onClick={onFocus}
      role={onFocus ? "button" : undefined}
      tabIndex={onFocus ? 0 : undefined}
      onKeyDown={onFocus ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onFocus(); } } : undefined}
      style={{
        // Selected → the whole card lifts like paper floating above paper (Apple
        // Wallet): a lighter surface, a soft ambient shadow, a whisper of elevation.
        // No gold outline as the hero, the lift itself says "you're here".
        display: "flex", alignItems: "center", gap: 10, cursor: onFocus ? "pointer" : "default",
        outline: "none",
        padding: "9px 12px", margin: "0 -12px 8px", borderRadius: 12,
        border: "1px solid transparent",
        background: active ? (darkMode ? "rgba(245,240,232,0.07)" : "rgba(255,255,255,0.72)") : "transparent",
        boxShadow: active
          ? (darkMode
              ? "0 10px 26px rgba(0,0,0,0.36), 0 2px 6px rgba(0,0,0,0.24)"
              : "0 10px 26px rgba(26,23,20,0.13), 0 2px 6px rgba(26,23,20,0.07)")
          : "none",
        transform: active ? "translateY(-2px)" : "none",
        transition: "background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
      }}
    >
      {thumb}
      <div style={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
        <div style={{ fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: subColor, marginBottom: 3 }}>
          <RoleMark filled />{WED_ROLE[role] || role}
        </div>
        <span style={{ fontFamily: "var(--font-heading-primary)", fontSize: 18, fontWeight: 500, color: textColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
          {entity}
        </span>
      </div>
      <StateBadge state={state} darkMode={darkMode} />
    </div>
  );
}

// An Explore row, a full thumbnail invites exploration (guide / spotlight / real wedding).
function ExploreRow({ label, title, image, href, darkMode }) {
  const T = getT(darkMode);
  const titleColor = darkMode ? "rgba(245,240,232,0.82)" : "rgba(26,23,20,0.82)";
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
       style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none", marginBottom: 10 }}>
      <div aria-hidden="true" style={{
        width: 54, height: 42, borderRadius: 6, flexShrink: 0,
        background: image ? `center/cover no-repeat url(${imgSrc(image)})` : "#2a2622",
        border: `1px solid ${T.divider}`,
      }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: GOLD, opacity: 0.75, marginBottom: 2, fontFamily: "var(--font-body)" }}>{label}</div>
        <div style={{ fontFamily: "var(--font-heading-primary)", fontSize: 14, color: titleColor, lineHeight: 1.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{title}</div>
      </div>
    </a>
  );
}

// An empty slot, a role still to fill (quiet, invites progress).
function NeededSlot({ role, label, darkMode }) {
  const faint = darkMode ? "rgba(245,240,232,0.3)" : "rgba(26,23,20,0.3)";
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
      <span style={{ fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: faint }}><RoleMark filled={false} />{WED_ROLE[role] || role}</span>
      <span style={{ fontFamily: "var(--font-body)", fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: faint }}>{label}</span>
    </div>
  );
}

// Golden-rule state, candidates existed but none could be justified for THIS
// wedding, so Aura honestly keeps looking rather than surface a weak match.
function SearchingSlot({ role, darkMode }) {
  const faint = darkMode ? "rgba(245,240,232,0.3)" : "rgba(26,23,20,0.3)";
  const italic = darkMode ? "rgba(245,240,232,0.45)" : "rgba(26,23,20,0.45)";
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: faint, marginBottom: 4 }}><RoleMark filled={false} />{WED_ROLE[role] || role}</div>
      <div style={{ fontFamily: "var(--font-heading-primary)", fontStyle: "italic", fontSize: 13.5, color: italic, lineHeight: 1.35 }}>
        Aura is still finding a {(WED_ROLE[role] || role).toLowerCase()} that truly fits your wedding.
      </div>
    </div>
  );
}

// A candidate slot, a transient recommendation offered for a role. Shortlist
// adopts it into the team (permanent); Not for us dismisses it.
function CandidateSlot({ role, item, state, reasoning, darkMode, onShortlist, onReject, onFocus, active }) {
  const textColor = darkMode ? "#f5f0e8" : "#1A1714";
  const label = darkMode ? "rgba(245,240,232,0.42)" : "rgba(26,23,20,0.42)";
  const faint = darkMode ? "rgba(245,240,232,0.32)" : "rgba(26,23,20,0.34)";
  const body  = darkMode ? "rgba(245,240,232,0.82)" : "rgba(26,23,20,0.8)";
  const hairline = "rgba(211,154,33,0.14)";
  const reasons = (reasoning?.reasons || []).map(reasonPhrase);
  const microLabel = { fontFamily: "var(--font-body)", fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: faint };

  return (
    <div style={{
      border: "1px solid rgba(211,154,33,0.3)", borderRadius: "var(--lwd-radius-card)",
      background: darkMode ? "rgba(211,154,33,0.05)" : "rgba(211,154,33,0.045)",
      padding: "13px 15px 14px", marginBottom: 18,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ ...microLabel, color: label }}><RoleMark filled={false} />{WED_ROLE[role] || role}</span>
        <StateBadge state={state} darkMode={darkMode} />
      </div>

      <div style={{ height: 1, background: hairline, margin: "12px 0" }} />

      <div style={{ ...microLabel, marginBottom: 5 }}>Aura recommends</div>
      <div
        onClick={onFocus}
        role={onFocus ? "button" : undefined}
        tabIndex={onFocus ? 0 : undefined}
        onKeyDown={onFocus ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onFocus(); } } : undefined}
        style={{ fontFamily: "var(--font-heading-primary)", fontSize: 20, fontWeight: 500, color: textColor, lineHeight: 1.15, cursor: onFocus ? "pointer" : "default", outline: "none", display: "inline-block", borderBottom: active ? `1px solid ${GOLD}` : "1px solid transparent", paddingBottom: 1, transition: "border-color 0.2s" }}>
        {item.name}
      </div>

      {reasons.length > 0 && (
        <>
          <div style={{ height: 1, background: hairline, margin: "12px 0" }} />
          <div style={{ ...microLabel, marginBottom: 8 }}>Why they fit your wedding</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {reasons.map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
                <span aria-hidden="true" style={{ color: GOLD, fontSize: 13, lineHeight: 1.35, flexShrink: 0 }}>✓</span>
                <span style={{ fontFamily: "var(--font-body)", fontSize: 12.5, color: body, lineHeight: 1.35 }}>{r}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 15 }}>
        <button
          onClick={() => onShortlist(item, reasoning)}
          style={{
            fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase",
            fontWeight: 700, color: "#0D0B09", background: GOLD, border: "none",
            borderRadius: "var(--lwd-radius-input)", padding: "8px 18px", cursor: "pointer",
          }}
        >
          Shortlist
        </button>
        <a href={candidateUrl(item)} target="_blank" rel="noopener noreferrer"
           style={{ fontFamily: "var(--font-body)", fontSize: 12, color: darkMode ? "rgba(245,240,232,0.55)" : "rgba(26,23,20,0.55)", textDecoration: "none" }}>
          View profile
        </a>
        <span
          onClick={() => onReject(item)}
          role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onReject(item); }}
          style={{ marginLeft: "auto", fontFamily: "var(--font-body)", fontSize: 11.5, color: faint, cursor: "pointer" }}
        >
          Not for us
        </span>
      </div>
    </div>
  );
}

// The right rail AS the wedding, recommendations live inside role slots, not a
// separate card list. The wedding is permanent; candidates are transient.
export function YourWeddingRail({ wedding, team = [], recItems = [], discover = {}, darkMode }) {
  const advanceTeam = useAura((s) => s._advanceTeamRole);
  const logReasoning = useAura((s) => s.logRecommendationReasoning);
  const rejectCandidate = useAura((s) => s.rejectCandidate);
  const persistedDismissed = useAura((s) => s.weddingDismissedIds);
  const persistedCandidates = useAura((s) => s.weddingCandidates);
  // Entity Focus, selecting an entity recomposes the right column into its dossier.
  const focusEntity   = useAura((s) => s.focusEntity);
  const focusedEntity = useAura((s) => s.focusedEntity);
  const focusOn = (e) => { if (e?.id) focusEntity({ id: e.id, name: e.name, type: e.type, category: e.category ?? e.categorySlug, imgs: e.imgs }); };
  // Session-local dismissals for instant feedback, merged with the persisted set
  // (rehydrated from candidate_rejected events) so "Not for us" survives reload.
  const [dismissed, setDismissed] = useState(() => new Set());
  const dismissedAll = useMemo(() => new Set([...(persistedDismissed || []), ...dismissed]), [persistedDismissed, dismissed]);
  const [imageMap, setImageMap] = useState({});
  const T = getT(darkMode);

  // Fetch hero images for team members that have JOINED (settled), thumbnails
  // are a reward for joining, so we only look them up once a role is filled.
  useEffect(() => {
    const ids = (team || []).filter((t) => t.entity_id && SETTLED_STATES.includes(t.state) && !(t.entity_id in imageMap)).map((t) => t.entity_id);
    if (!ids.length || !supabaseAnon) return;
    let cancelled = false;
    supabaseAnon.from("listings").select("id,hero_image,hero_images").in("id", ids).then(({ data }) => {
      if (cancelled || !data) return;
      setImageMap((prev) => { const n = { ...prev }; for (const l of data) n[l.id] = l.hero_image || (Array.isArray(l.hero_images) && l.hero_images[0]?.url) || null; return n; });
    });
    return () => { cancelled = true; };
  }, [team]); // eslint-disable-line react-hooks/exhaustive-deps
  const textColor = darkMode ? "#f5f0e8" : "#1A1714";
  const faint = darkMode ? "rgba(245,240,232,0.3)" : "rgba(26,23,20,0.32)";

  const subline = [];
  if (wedding.guest_count)   subline.push(`${wedding.guest_count} guests`);
  const timing = describeTiming(wedding);
  if (timing)                subline.push(timing);          // the heartbeat, in the couple's own words
  if (wedding.current_stage) subline.push(wedding.current_stage);
  if (wedding.style)         subline.push(wedding.style);
  if (wedding.budget_amount) subline.push(`${wedding.budget_currency || ""}${Number(wedding.budget_amount).toLocaleString()}`);

  const teamByRole = {};
  (team || []).forEach((t) => { teamByRole[t.role] = t; });

  const liveByRole = {};
  (recItems || []).forEach((it) => {
    if (dismissedAll.has(it.id)) return;
    const r = roleFromCategory(it.categorySlug || it.category);
    (liveByRole[r] = liveByRole[r] || []).push(it);
  });
  const focusRole = (recItems && recItems.length) ? roleFromCategory(recItems[0].categorySlug || recItems[0].category) : null;

  // Image source for filled slots, prefer the live rec (it carries full images),
  // fall back to the async hero-image lookup (for resume, once recs are gone).
  const recImg = {};
  (recItems || []).forEach((it) => { const im = it.imgs?.[0] || (Array.isArray(it.hero_images) && it.hero_images[0]?.url); if (im) recImg[it.id] = im; });

  const active = new Set([...Object.keys(teamByRole), ...(focusRole ? [focusRole] : [])]);
  const shown = WED_ROLE_ORDER.filter((r) => r !== "vendor" && active.has(r));
  const comingUp = WED_ROLE_ORDER.filter((r) => r !== "vendor" && !active.has(r)).slice(0, 2);

  const weddingCountry = countryOf(wedding.destination) || (recItems.find((i) => i.country)?.country?.toLowerCase() || null);
  const onShortlist = (item, reasoning) => { advanceTeam?.(item, "shortlisted"); if (reasoning) logReasoning?.(reasoning); };
  const onReject = (item) => {
    setDismissed((prev) => { const n = new Set(prev); n.add(item.id); return n; }); // instant
    rejectCandidate?.(item);                                                         // persist as evidence
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
        <Icon name="sparkleMini" size={13} color={GOLD} />
        <span style={{ fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD }}>Your Wedding</span>
      </div>
      {wedding.destination && (
        <div style={{ fontFamily: "var(--font-heading-primary)", fontSize: 27, fontWeight: 500, color: textColor, lineHeight: 1.1, marginBottom: 7 }}>
          {wedding.destination}
        </div>
      )}
      {subline.length > 0 && (
        <div style={{ fontFamily: "var(--font-body)", fontSize: 12.5, color: textColor, opacity: 0.55, marginBottom: 8 }}>
          {subline.join("  ·  ")}
        </div>
      )}
      <div style={{ fontFamily: "var(--font-heading-primary)", fontStyle: "italic", fontSize: 12.5, color: darkMode ? "rgba(245,240,232,0.82)" : "rgba(26,23,20,0.78)", lineHeight: 1.45, marginBottom: 16 }}>
        {weddingGuidance(wedding, team) || "Aura is quietly assembling your wedding as you chat."}
      </div>
      <div style={{ height: 1, background: "rgba(211,154,33,0.16)", marginBottom: 18 }} />

      {shown.map((role) => {
        const t = teamByRole[role];
        const settled = t && SETTLED_STATES.includes(t.state);
        // A candidate the couple rejected stays hidden, the slot reverts to Needed
        // until Aura suggests another.
        const activeRejected = t && !settled && t.entity_id && dismissedAll.has(t.entity_id);
        const best = (!settled && !activeRejected && liveByRole[role]) ? bestCandidate(wedding, liveByRole[role], role, weddingCountry) : null;
        if (best) return <CandidateSlot key={role} role={role} item={best.item} reasoning={best.reasoning} state={t?.state || "exploring"} darkMode={darkMode} onShortlist={onShortlist} onReject={onReject} onFocus={() => focusOn(best.item)} active={focusedEntity?.id === best.item?.id} />;
        // Persisted candidate, the gold box rehydrated from the event log so a live
        // suggestion survives reload/resume (no live rec needed).
        const pc = (!settled && !activeRejected && persistedCandidates?.[role] && !dismissedAll.has(persistedCandidates[role].item?.id)) ? persistedCandidates[role] : null;
        if (pc) return <CandidateSlot key={role} role={role} item={pc.item} reasoning={pc.reasoning} state={t?.state || "exploring"} darkMode={darkMode} onShortlist={onShortlist} onReject={onReject} onFocus={() => focusOn(pc.item)} active={focusedEntity?.id === pc.item?.id} />;
        if (settled && t?.entity_name) return <FilledSlot key={role} role={role} entity={t.entity_name} state={t.state} image={imageMap[t.entity_id] || recImg[t.entity_id]} darkMode={darkMode} onFocus={() => focusOn({ id: t.entity_id, name: t.entity_name })} active={focusedEntity?.id === t.entity_id} />;
        // Golden rule: candidates existed this turn but none could be justified → keep looking.
        if (!settled && !activeRejected && liveByRole[role] && liveByRole[role].length) return <SearchingSlot key={role} role={role} darkMode={darkMode} />;
        if (t && t.entity_name && !activeRejected) return <FilledSlot key={role} role={role} entity={t.entity_name} state={t.state} image={imageMap[t.entity_id] || recImg[t.entity_id]} darkMode={darkMode} onFocus={() => focusOn({ id: t.entity_id, name: t.entity_name })} active={focusedEntity?.id === t.entity_id} />;
        return <NeededSlot key={role} role={role} label="Needed" darkMode={darkMode} />;
      })}

      {comingUp.map((role) => <NeededSlot key={role} role={role} label="Needed" darkMode={darkMode} />)}
      {/* Knowledge (editorial / real weddings / spotlights) no longer lives here,
          it moved to the right column, headless and contextual. The left rail is
          the couple's own space: their wedding, nothing else. */}
    </div>
  );
}

// ── The wedding, in pictures, the living canvas's emotional layer ──────────
// The roster names the team; this shows what the wedding is becoming. It draws
// ONLY on the real imagery of the entities the wedding already holds (settled
// team members), no stock, no fabrication. A collage that fills as the team
// grows. Clicking a frame focuses that entity's dossier (same as the roster).
// Canvas-only, Private-only. Read-only fetch; listing pages untouched.
function collectEntityImages(listing, cap = 5) {
  const out = [];
  const push = (u) => { const s = typeof u === "string" ? u : (u?.url || u?.src || u?.image_url); if (s && !out.includes(s)) out.push(s); };
  if (listing?.hero_image) push(listing.hero_image);
  (Array.isArray(listing?.hero_images) ? listing.hero_images : []).forEach(push);
  (Array.isArray(listing?.media_items) ? listing.media_items : [])
    .filter((m) => m?.type !== "video" && m?.visibility !== "private")
    .forEach((m) => push(m?.url));
  return out.slice(0, cap);
}

export function WeddingMood({ team = [], darkMode, onFocus }) {
  const [byEntity, setByEntity] = useState({});   // entity_id -> { name, role, images: [] }
  const faint = darkMode ? "rgba(245,240,232,0.5)" : "rgba(26,23,20,0.5)";
  const border = darkMode ? "rgba(211,154,33,0.16)" : "rgba(26,23,20,0.12)";

  const settled = (team || []).filter((t) => t.entity_id && SETTLED_STATES.includes(t.state));
  const settledIds = settled.map((t) => t.entity_id).join(",");

  useEffect(() => {
    const ids = settled.map((t) => t.entity_id).filter((id) => !(id in byEntity));
    if (!ids.length || !supabaseAnon) return;
    let cancelled = false;
    supabaseAnon.from("listings").select("id,name,hero_image,hero_images,media_items").in("id", ids).then(({ data }) => {
      if (cancelled || !data) return;
      setByEntity((prev) => {
        const n = { ...prev };
        for (const l of data) {
          const t = settled.find((s) => s.entity_id === l.id);
          n[l.id] = { name: l.name || t?.entity_name || "", role: t?.role, images: collectEntityImages(l) };
        }
        return n;
      });
    });
    return () => { cancelled = true; };
  }, [settledIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Interleave one image per entity per pass, so a big team never gets buried
  // under one venue's gallery, the collage reads as the whole wedding.
  const frames = [];
  const pools = settled
    .map((t) => ({ id: t.entity_id, name: t.entity_name, role: t.role, images: byEntity[t.entity_id]?.images || [], idx: 0 }))
    .filter((p) => p.images.length);
  let added = true;
  while (added && frames.length < 7) {
    added = false;
    for (const p of pools) {
      if (frames.length >= 7) break;
      if (p.idx < p.images.length) { frames.push({ src: p.images[p.idx], id: p.id, name: p.name, role: p.role }); p.idx++; added = true; }
    }
  }

  if (!frames.length) return null;
  const [hero, ...rest] = frames;
  const focus = (f) => { if (f?.id && onFocus) onFocus({ id: f.id, name: f.name }); };
  const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

  // Every frame carries a persistent caption, which company or venue it is,
  // over a soft bottom scrim so the name stays legible on any image. The scrim is
  // restrained (imagery still leads); the title attr keeps the full name on hover.
  const Frame = ({ f, style, big = false }) => (
    <div onClick={() => focus(f)} role={onFocus ? "button" : undefined} tabIndex={onFocus ? 0 : undefined}
      onKeyDown={onFocus ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); focus(f); } } : undefined}
      title={f.name}
      style={{ position: "relative", borderRadius: 8, overflow: "hidden", cursor: onFocus ? "pointer" : "default",
        background: `center/cover no-repeat url(${imgSrc(f.src)})`, border: `1px solid ${border}`, outline: "none", ...style }}>
      {f?.name && (
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: big ? "20px 12px 9px" : "16px 7px 6px",
          background: "linear-gradient(to top, rgba(13,11,9,0.74) 0%, rgba(13,11,9,0.34) 45%, rgba(13,11,9,0) 100%)",
          pointerEvents: "none" }}>
          <div style={{ fontFamily: "var(--font-body)", fontSize: big ? 11 : 9, fontWeight: 600, letterSpacing: "0.03em",
            color: "rgba(255,255,255,0.96)", lineHeight: 1.2, textShadow: "0 1px 3px rgba(0,0,0,0.55)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {f.name}{big && f.role ? `  ·  ${cap(WED_ROLE[f.role] || f.role)}` : ""}
          </div>
        </div>
      )}
    </div>
  );

  return (
    /* No heading, luxury trusts the image. The collage simply arrives after the
       roster, separated by breathing room rather than a label. Each frame names
       its own company/venue over a soft scrim. */
    <div style={{ marginTop: 30 }}>
      <Frame f={hero} big style={{ width: "100%", aspectRatio: "16 / 9", marginBottom: rest.length ? 12 : 0 }} />
      {rest.length > 0 && (() => {
        // Keep the collage to full rows (3 or 6) so it never leaves an orphan
        // tile, a clean grid reads more premium than a ragged one.
        const gridN = rest.length >= 6 ? 6 : rest.length >= 3 ? 3 : rest.length;
        return (
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(3, gridN)}, 1fr)`, gap: 6, marginTop: 12 }}>
            {rest.slice(0, gridN).map((f, i) => <Frame key={i} f={f} style={{ aspectRatio: "1 / 1" }} />)}
          </div>
        );
      })()}
    </div>
  );
}

// ── The path ahead, the living canvas's forward motion ─────────────────────
// The roster answers "who is on the team, in what state". This answers the next
// question a couple actually asks: "so what do I do next?" It reads the SAME team
// states, but renders them as a JOURNEY, where you are, the single next step
// (with a real action that sends the query to Aura and advances the wedding), and
// the road still ahead. Pure read of state; the only side effect is the couple
// choosing to act. Canvas-only, Private-only. Never touches the roster.
const PATH_ORDER = ["venue", "planner", "photographer", "videographer", "florist", "catering", "entertainment", "celebrant"];
const PATH_ASK = {
  venue:        "villa or venue",
  planner:      "wedding planner",
  photographer: "wedding photographer",
  videographer: "wedding videographer",
  florist:      "florist",
  catering:     "caterer",
  entertainment:"band or entertainment",
  celebrant:    "celebrant",
};
// One warm line on why this step matters now. No em-dashes (house style).
const PATH_NUDGE = {
  venue:        "It anchors everything else, so it comes first.",
  planner:      "A planner holds all the threads together.",
  photographer: "It is how the whole day is remembered.",
  videographer: "Film keeps the moments that stills cannot.",
  florist:      "Florals set the mood of the entire day.",
  catering:     "Food and drink shape how the day feels.",
  entertainment:"It carries the celebration into the night.",
  celebrant:    "A celebrant makes the ceremony truly yours.",
};

export function WeddingPath({ wedding, team = [], darkMode, onAsk }) {
  if (!wedding) return null;
  const text  = darkMode ? "#f5f0e8" : "#1A1714";
  const faint = darkMode ? "rgba(245,240,232,0.3)" : "rgba(26,23,20,0.32)";
  const body  = darkMode ? "rgba(245,240,232,0.82)" : "rgba(26,23,20,0.8)";
  const sub   = darkMode ? "rgba(245,240,232,0.5)" : "rgba(26,23,20,0.5)";

  const state = {};
  (team || []).forEach((t) => { if (t?.role) state[t.role] = t.state; });
  const secured = (r) => SETTLED_STATES.includes(state[r]);
  const active  = (r) => secured(r) || state[r] === "exploring" || state[r] === "suggested";

  const nextGap = PATH_ORDER.find((r) => !active(r));
  const ahead   = PATH_ORDER.filter((r) => r !== nextGap && !active(r));
  const securedCount = PATH_ORDER.filter(secured).length;
  const inPlay = PATH_ORDER.filter((r) => active(r) && !secured(r)).length;

  // Momentum line, reads the wedding's progress, never a score.
  const momentum = securedCount > 0
    ? `${securedCount} chosen${inPlay ? ` · ${inPlay} in progress` : ""}${ahead.length ? ` · ${ahead.length} still ahead` : ""}`
    : (inPlay ? `${inPlay} in progress` : "Your wedding is just beginning.");

  return (
    <div style={{ marginTop: 30 }}>
      <div style={{ height: 1, background: "rgba(211,154,33,0.16)", marginBottom: 22 }} />
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 16 }}>
        <span style={{ fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD }}>The path ahead</span>
        <span style={{ fontFamily: "var(--font-body)", fontSize: 10.5, letterSpacing: "0.04em", color: sub }}>{momentum}</span>
      </div>

      {nextGap ? (
        <>
          {/* The single next step, the only thing asking to be done now. It carries
              a real action: sending the query to Aura, which advances the wedding. */}
          <div style={{
            border: "1px solid rgba(211,154,33,0.3)", borderRadius: "var(--lwd-radius-card)",
            background: darkMode ? "rgba(211,154,33,0.05)" : "rgba(211,154,33,0.045)",
            padding: "15px 17px 16px", marginBottom: ahead.length ? 18 : 4,
          }}>
            <div style={{ fontFamily: "var(--font-body)", fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: faint, marginBottom: 6 }}>Next step</div>
            <div style={{ fontFamily: "var(--font-heading-primary)", fontSize: 21, fontWeight: 500, color: text, lineHeight: 1.15, marginBottom: 7 }}>
              Find your {(WED_ROLE[nextGap] || nextGap).toLowerCase()}
            </div>
            <div style={{ fontFamily: "var(--font-heading-primary)", fontStyle: "italic", fontSize: 13, color: body, lineHeight: 1.5, marginBottom: 15 }}>
              {PATH_NUDGE[nextGap]}
            </div>
            <button
              onClick={() => onAsk?.(nextGap)}
              style={{
                fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase",
                fontWeight: 700, color: "#0D0B09", background: GOLD, border: "none", outline: "none",
                borderRadius: "var(--lwd-radius-input)", padding: "9px 20px", cursor: "pointer",
              }}
            >
              Ask Aura
            </button>
          </div>

          {/* The road still ahead, quiet, so the couple sees the journey without
              feeling a to-do list. Order is how a wedding is actually planned. */}
          {ahead.length > 0 && (
            <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: faint, lineHeight: 1.7 }}>
              <span style={{ letterSpacing: "0.04em" }}>Then, in time: </span>
              {ahead.map((r, i) => (
                <span key={r}>{(WED_ROLE[r] || r).toLowerCase()}{i < ahead.length - 1 ? ", " : "."}</span>
              ))}
            </div>
          )}
        </>
      ) : (
        <div style={{ fontFamily: "var(--font-heading-primary)", fontStyle: "italic", fontSize: 14, color: body, lineHeight: 1.55 }}>
          Your team is complete. Everything from here is refinement, reach out to anyone whenever you are ready.
        </div>
      )}
    </div>
  );
}

// ── Earlier ideas, the Guardian's preserved directions, made visible ────────
// Nothing is ever lost: the directions the couple explored and moved on from sit
// here, one tap from coming back. Deliberately quiet (Hermès: hold everything in
// the machinery, show almost nothing), never the word "scenario". Canvas + Private
// only. Renders nothing until there is something to restore.
// ── Directions, the couple's other versions of their wedding ────────────────
// A conversation is NOT the unit here; a DIRECTION is (Italy vs Lake Como vs
// Mallorca), each a complete wedding the Guardian preserved. Chat is just one of
// its properties. Opening one restores that whole wedding + its thread. Never the
// words "conversation" or "idea". Renders nothing until a direction exists.
export function WeddingDirections({ directions = [], darkMode, onOpen }) {
  if (!directions.length) return null;
  const text  = darkMode ? "#f5f0e8" : "#1A1714";
  const faint = darkMode ? "rgba(245,240,232,0.55)" : "rgba(26,23,20,0.5)";
  const nameOf = (d) => d.label || d.destination || "A direction";
  const subOf  = (d) => [d.style, d.guest_count ? `${d.guest_count} guests` : null].filter(Boolean).join("  ·  ");
  return (
    <div style={{ marginTop: 30 }}>
      <div style={{ height: 1, background: "rgba(211,154,33,0.16)", marginBottom: 18 }} />
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
        <span style={{ fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD }}>Directions you've explored</span>
        <span style={{ fontFamily: "var(--font-body)", fontSize: 10.5, letterSpacing: "0.04em", color: faint }}>held for you</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {directions.map((d) => (
          <div key={d.id}
            onClick={() => onOpen?.(d.id)} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(d.id); } }}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, cursor: "pointer", outline: "none", padding: "9px 12px", margin: "0 -12px", borderRadius: 10, transition: "background 0.18s ease" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = darkMode ? "rgba(245,240,232,0.045)" : "rgba(26,23,20,0.035)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "var(--font-heading-primary)", fontSize: 16, color: text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{nameOf(d)}</div>
              {subOf(d) && <div style={{ fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.03em", color: faint, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{subOf(d)}</div>}
            </div>
            <span style={{ fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.06em", color: GOLD, flexShrink: 0 }}>Open</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Toward the day, the forward spine of the whole wedding ──────────────────
// The milestones in planning order, marked from real team state, ending at the day
// itself (anchored by the date). Where "Our Journey" is the past, this is the arc
// ahead. Canvas + Private only. Derived, no fabrication.
const TIMELINE_ROLES = [["venue", "Venue"], ["planner", "Planner"], ["photographer", "Photographer"], ["florist", "Florist"], ["catering", "Food & drink"], ["entertainment", "Entertainment"]];
export function WeddingTimeline({ wedding, team = [], darkMode }) {
  if (!wedding) return null;
  const state = {};
  (team || []).forEach((t) => { if (t?.role) state[t.role] = t.state; });
  const statusOf = (r) => {
    const s = state[r];
    if (SETTLED_STATES.includes(s)) return "done";
    if (s === "exploring" || s === "suggested") return "progress";
    return "upcoming";
  };
  const roleStops = TIMELINE_ROLES.map(([r, label]) => ({ label, status: statusOf(r) }));
  // The current stop is the first one not yet done.
  const currentIdx = roleStops.findIndex((s) => s.status !== "done");
  if (currentIdx >= 0 && roleStops[currentIdx].status === "upcoming") roleStops[currentIdx].status = "current";
  const stops = [
    { label: "Planning started", status: "done" },
    ...roleStops,
    { label: "Your wedding day", status: "end", sub: describeTiming(wedding) },
  ];

  const text  = darkMode ? "#f5f0e8" : "#1A1714";
  const faint = darkMode ? "rgba(245,240,232,0.34)" : "rgba(26,23,20,0.36)";
  const body  = darkMode ? "rgba(245,240,232,0.7)" : "rgba(26,23,20,0.68)";
  const dot = (status) => {
    const base = { width: 14, height: 14, borderRadius: "50%", flexShrink: 0, boxSizing: "border-box", marginTop: 1, zIndex: 1, position: "relative", background: darkMode ? "#0D0B09" : "#f3efeb" };
    if (status === "done")    return { ...base, background: GOLD, border: `1px solid ${GOLD}` };
    if (status === "current") return { ...base, border: `2px solid ${GOLD}`, boxShadow: `0 0 0 3px rgba(211,154,33,0.16)` };
    if (status === "progress")return { ...base, border: `2px solid ${GOLD}` };
    if (status === "end")     return { ...base, width: 12, height: 12, marginTop: 2, transform: "rotate(45deg)", borderRadius: 2, background: GOLD, border: `1px solid ${GOLD}` };
    return { ...base, border: `1px solid ${darkMode ? "rgba(245,240,232,0.28)" : "rgba(26,23,20,0.24)"}` };
  };
  const labelColor = (status) => (status === "upcoming" ? faint : status === "end" ? GOLD : text);

  return (
    <div style={{ marginTop: 30 }}>
      <div style={{ height: 1, background: "rgba(211,154,33,0.16)", marginBottom: 20 }} />
      <div style={{ fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD, marginBottom: 18 }}>Toward the day</div>
      <div style={{ position: "relative", paddingLeft: 3 }}>
        <div aria-hidden="true" style={{ position: "absolute", left: 10, top: 10, bottom: 12, width: 1, background: "rgba(211,154,33,0.22)" }} />
        {stops.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: i === stops.length - 1 ? 0 : 15 }}>
            <div style={dot(s.status)} />
            <div style={{ paddingTop: 0 }}>
              <div style={{ fontFamily: s.status === "end" ? "var(--font-heading-primary)" : "var(--font-body)", fontSize: s.status === "end" ? 15 : 13, color: labelColor(s.status), letterSpacing: s.status === "end" ? 0 : "0.01em" }}>{s.label}</div>
              {s.sub && <div style={{ fontFamily: "var(--font-body)", fontSize: 11, color: body, marginTop: 2 }}>{s.sub}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Our Journey, the event log read back as the couple's own notebook ───────
// Day-grouped sentences, each a fact from the log (never an invented why). She
// never sees "event" or "narrative memory". Canvas + Private only; renders nothing
// until there's a story to tell.
export function WeddingJourney({ journey = [], darkMode }) {
  if (!journey.length) return null;
  const body  = darkMode ? "rgba(245,240,232,0.82)" : "rgba(26,23,20,0.8)";
  const faint = darkMode ? "rgba(245,240,232,0.38)" : "rgba(26,23,20,0.4)";
  return (
    <div style={{ marginTop: 30 }}>
      <div style={{ height: 1, background: "rgba(211,154,33,0.16)", marginBottom: 20 }} />
      <div style={{ fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD, marginBottom: 18 }}>Our Journey</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {journey.map((d, i) => (
          <div key={i} style={{ display: "flex", gap: 14 }}>
            <div style={{ fontFamily: "var(--font-body)", fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: faint, minWidth: 46, paddingTop: 3, flexShrink: 0 }}>{d.day}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {d.lines.map((l, j) => (
                <div key={j} style={{ fontFamily: "var(--font-heading-primary)", fontSize: 13.5, color: body, lineHeight: 1.45 }}>{l}</div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── ShortlistBar, shown when 2+ venues are Aura-shortlisted ─────────────────
function ShortlistBar({ items, darkMode, onEnquireAll, onCompare }) {
  const T = getT(darkMode);
  const clearAuraShortlist = useAura((s) => s.clearAuraShortlist);
  const auraShortlistRemove = useAura((s) => s.auraShortlistRemove);

  return (
    <div style={{
      flexShrink:  0,
      padding:     "12px 14px 14px",
      borderTop:   `1px solid rgba(211,154,33,0.2)`,
      background:  darkMode ? "rgba(211,154,33,0.04)" : "rgba(211,154,33,0.03)",
    }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 9 }}>
        <span style={{
          fontFamily:    "var(--font-body)",
          fontSize:      9,
          fontWeight:    700,
          letterSpacing: "1.4px",
          textTransform: "uppercase",
          color:         GOLD,
        }}>
          Your Shortlist ({items.length})
        </span>
        <button
          onClick={() => clearAuraShortlist()}
          style={{
            background:  "none",
            border:      "none",
            cursor:      "pointer",
            fontFamily:  "var(--font-body)",
            fontSize:    9,
            color:       T.grey,
            opacity:     0.55,
            padding:     "2px 0",
          }}
        >
          Clear all
        </button>
      </div>

      {/* Thumbnails */}
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        {items.map((item) => (
          <div
            key={item.id}
            title={item.name}
            style={{ position: "relative", flexShrink: 0 }}
          >
            <div style={{
              width:        38,
              height:       38,
              borderRadius: 6,
              overflow:     "hidden",
              border:       `1px solid rgba(211,154,33,0.35)`,
              background:   "#0a0806",
            }}>
              {item.imgs?.[0] && (
                <img
                  src={imgSrc(item.imgs[0])}
                  alt={item.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </div>
            {/* Remove chip */}
            <button
              onClick={() => auraShortlistRemove(item.id)}
              aria-label={`Remove ${item.name} from shortlist`}
              style={{
                position:       "absolute",
                top:            -5,
                right:          -5,
                width:          14,
                height:         14,
                borderRadius:   "50%",
                background:     "rgba(13,11,9,0.85)",
                border:         `1px solid rgba(211,154,33,0.35)`,
                color:          "rgba(255,255,255,0.7)",
                cursor:         "pointer",
                fontSize:       8,
                display:        "flex",
                alignItems:     "center",
                justifyContent: "center",
                lineHeight:     1,
                padding:        0,
              }}
            >
              ×
            </button>
          </div>
        ))}
        {/* Empty slot indicators up to 3 */}
        {Array.from({ length: Math.max(0, 3 - items.length) }).map((_, i) => (
          <div
            key={`slot-${i}`}
            style={{
              width:        38,
              height:       38,
              borderRadius: 6,
              border:       `1px dashed rgba(211,154,33,0.2)`,
              background:   "transparent",
              flexShrink:   0,
            }}
          />
        ))}
      </div>

      {/* Compare + Enquire row */}
      <div style={{ display: "flex", gap: 6 }}>
        {/* Compare, pushes into the existing compare strip */}
        <button
          onClick={onCompare}
          style={{
            flex:          "0 0 auto",
            padding:       "9px 12px",
            background:    "transparent",
            border:        `1px solid rgba(211,154,33,0.3)`,
            borderRadius:  "var(--lwd-radius-input)",
            fontFamily:    "var(--font-body)",
            fontSize:      10,
            fontWeight:    700,
            letterSpacing: "1px",
            textTransform: "uppercase",
            color:         GOLD,
            cursor:        "pointer",
            transition:    "all 0.2s",
            whiteSpace:    "nowrap",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.background = "rgba(211,154,33,0.06)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(211,154,33,0.3)"; e.currentTarget.style.background = "transparent"; }}
        >
          Compare
        </button>

        {/* Enquire to all CTA */}
        <button
          onClick={onEnquireAll}
          style={{
            flex:          1,
            padding:       "9px 0",
            background:    GOLD,
            border:        "none",
            borderRadius:  "var(--lwd-radius-input)",
            fontFamily:    "var(--font-body)",
            fontSize:      10,
            fontWeight:    700,
            letterSpacing: "1px",
            textTransform: "uppercase",
            color:         "#0D0B09",
            cursor:        "pointer",
            transition:    "opacity 0.2s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.88"; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
        >
          Enquire to All {items.length}
        </button>
      </div>
    </div>
  );
}

// Collect image URLs from the varied shapes a listing carries.
const capWord = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

// A dossier section, small content-type label (Gallery, Story…) then the content.
// The labels name things brides think about, never the system.
function Sect({ label, faint, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ fontFamily: "var(--font-body)", fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: faint, marginBottom: 10 }}>{label}</div>
      {children}
    </div>
  );
}

// Entity Focus, the right column recomposes into everything LWD already knows
// about the selected entity. No fabrication, no graph: its own dossier, reorganised.
function EntityDossier({ entity, darkMode, onBack }) {
  const getTeamEvidence = useAura((s) => s.getTeamEvidence);
  const wedding         = useAura((s) => s.wedding);
  const [data, setData]         = useState(null);
  const [showcase, setShowcase] = useState(null);
  const [reasons, setReasons]   = useState([]);   // raw evidence objects; phrased at render
  const [videoIdx, setVideoIdx] = useState(null);   // index into videos, or null
  const [photoIdx, setPhotoIdx] = useState(null);   // index into gallery, or null
  const [storyOpen, setStoryOpen] = useState(false); // Story starts clamped to 3 lines
  const text  = darkMode ? "#f5f0e8" : "#1A1714";
  const faint = darkMode ? "rgba(245,240,232,0.5)"  : "rgba(26,23,20,0.5)";
  const body  = darkMode ? "rgba(245,240,232,0.82)" : "rgba(26,23,20,0.8)";

  useEffect(() => {
    let cancelled = false;
    setData(null); setShowcase(null); setReasons([]);
    if (!entity?.id || !supabaseAnon) return;
    supabaseAnon.from("listings")
      .select("name,slug,region,region_slug,country,country_slug,category_slug,styles,photo_styles,price_from,price_currency,planner_starting_from,photo_starting_from,media_starting_from,florist_starting_from,caterer_starting_from,ent_starting_from,short_description,description,hero_images,media_items,luxury_tier")
      .eq("id", entity.id).maybeSingle()
      .then(({ data }) => { if (!cancelled) setData(data || {}); });
    supabaseAnon.from("venue_showcases").select("slug,title").eq("listing_id", entity.id).in("status", ["live", "published"]).limit(1)
      .then(({ data }) => { if (!cancelled && data?.[0]) setShowcase(data[0]); });
    getTeamEvidence?.(entity.id).then((ev) => { if (!cancelled) setReasons(Array.isArray(ev?.reasons) ? ev.reasons : []); });
    return () => { cancelled = true; };
  }, [entity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reuse the platform's media UTILITIES (pure fns → listing pages untouched) AND
  // its standard players (VideoPlayModal / Lightbox) so a couple gets the same
  // rate + share experience. The inline grid is our own compact, theme-aware tiles;
  // clicking one opens the standard modal.
  const mediaItems = Array.isArray(data?.media_items) ? data.media_items : [];
  const videos = buildVenueVideos(mediaItems);
  let gallery = mediaItems
    .filter((i) => i?.type !== "video" && i?.visibility !== "private" && i?.url)
    .map((i) => mapMediaItemToGalleryPhoto(i));
  if (!gallery.length) {
    const raw = [...(data?.hero_images || []), ...(entity?.imgs || [])]
      .map((x) => (typeof x === "string" ? x : (x?.url || x?.src || x?.image_url))).filter(Boolean);
    gallery = [...new Set(raw)].map((src, idx) => ({ id: `img-${idx}`, src, alt: "" }));
  }
  const tiles = [
    ...videos.map((v) => ({ kind: "video", poster: v.thumb || v.thumbFallback || null })),
    ...gallery.map((g) => ({ kind: "image", poster: g.src })),
  ];
  const hasMedia = tiles.length > 0;
  const openTile = (i) => { if (i < videos.length) setVideoIdx(i); else setPhotoIdx(i - videos.length); };
  const styles = ((data?.photo_styles?.length ? data.photo_styles : data?.styles) || []).filter(Boolean).slice(0, 6);
  // Price: venues use price_from; each vendor type keeps its own starting_from. Coerce
  // safely (values can be formatted strings like "€8,000") and never render "NaN".
  const rawPrice = data?.price_from ?? data?.planner_starting_from ?? data?.photo_starting_from ?? data?.media_starting_from ?? data?.florist_starting_from ?? data?.caterer_starting_from ?? data?.ent_starting_from ?? entity?.priceFrom;
  const priceNum = typeof rawPrice === "number" ? rawPrice : Number(String(rawPrice ?? "").replace(/[^0-9.]/g, ""));
  const price = (Number.isFinite(priceNum) && priceNum > 0) ? `${data?.price_currency || "€"}${priceNum.toLocaleString()}` : null;
  const story  = (data?.description || data?.short_description || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() || null;
  const signals = [data?.luxury_tier, capWord(data?.region)].filter(Boolean).join("   ·   ");
  const name = entity?.name || data?.name || "";
  const profileHref = data?.slug ? candidateUrl({ slug: data.slug, category: data.category_slug, categorySlug: data.category_slug, countrySlug: data.country_slug, regionSlug: data.region_slug, type: entity?.type }) : null;

  // Editorial gallery, the imagery leads (a wedding professional is bought
  // visually). A wide hero frame, then a quiet grid; clicking any frame opens the
  // platform's standard player. A single overflow tile carries "+N".
  const tileBorder = darkMode ? "rgba(211,154,33,0.14)" : "rgba(26,23,20,0.12)";
  const renderTile = (m, i, { hero = false, overflow = 0 } = {}) => (
    <div key={i} onClick={() => openTile(i)} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openTile(i); } }}
      style={{ position: "relative", aspectRatio: hero ? "3 / 2" : "1 / 1", borderRadius: hero ? 10 : 8, background: m?.poster ? `center/cover no-repeat url(${imgSrc(m.poster)})` : "linear-gradient(135deg,#2a231a,#14100c)", border: `1px solid ${tileBorder}`, cursor: "pointer", outline: "none" }}>
      {m?.kind === "video" && !overflow && (
        <div aria-hidden="true" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ width: hero ? 46 : 30, height: hero ? 46 : 30, borderRadius: "50%", background: "rgba(8,6,5,0.5)", border: "1px solid rgba(245,240,232,0.65)", color: "#f5f0e8", fontSize: hero ? 16 : 11, display: "flex", alignItems: "center", justifyContent: "center", paddingLeft: 2 }}>▶</span>
        </div>
      )}
      {overflow > 0 && (
        <div style={{ position: "absolute", inset: 0, borderRadius: 8, background: "rgba(8,6,5,0.5)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-heading-primary)", fontSize: 17, color: "#f5f0e8" }}>+{overflow}</div>
      )}
    </div>
  );
  const restCount = Math.max(0, tiles.length - 1);
  const gridCols = Math.min(3, restCount || 1);
  // Phrase the stored "why" reasons against the ACTIVE wedding, a stale location
  // claim degrades to honest wording (or drops out) rather than asserting a place
  // the wedding has moved away from. Interim guard ahead of Wedding Scenarios.
  const whyPhrases = reasons.map((x) => reasonPhrase(x, { destination: wedding?.destination })).filter(Boolean);

  return (
    <div style={{ height: "100%", overflowY: "auto", padding: "22px 22px 26px" }}>
      <button onClick={onBack}
        style={{ background: "none", border: "none", cursor: "pointer", color: faint, fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.05em", padding: 0, marginBottom: 18, display: "flex", alignItems: "center", gap: 6 }}>
        ← Back
      </button>

      <div style={{ fontFamily: "var(--font-heading-primary)", fontSize: 25, fontWeight: 500, color: text, lineHeight: 1.1, marginBottom: signals ? 5 : 20 }}>{name}</div>
      {signals && <div style={{ fontFamily: "var(--font-body)", fontSize: 11.5, color: faint, marginBottom: 22 }}>{signals}</div>}

      {/* Loading + graceful quiet state, never a system-describing placeholder. */}
      {data === null && (
        <div style={{ fontFamily: "var(--font-heading-primary)", fontStyle: "italic", fontSize: 13, color: faint }}>Gathering everything on {name.split(" ")[0] || "them"}…</div>
      )}
      {data && !hasMedia && !story && !price && styles.length === 0 && reasons.length === 0 && !showcase && (
        <div style={{ fontFamily: "var(--font-heading-primary)", fontStyle: "italic", fontSize: 13.5, color: faint, lineHeight: 1.55 }}>
          There's more to come on {name.split(" ")[0] || "them"}.{profileHref ? " Their full profile has everything we know so far." : ""}
        </div>
      )}

      {hasMedia && (
        <div style={{ marginBottom: 26 }}>
          {renderTile(tiles[0], 0, { hero: true })}
          {restCount > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${gridCols}, 1fr)`, gap: 6, marginTop: 6 }}>
              {tiles.slice(1, 7).map((m, idx) => {
                const i = idx + 1;
                const isOverflow = i === 6 && tiles.length > 7;
                return renderTile(m, i, { overflow: isOverflow ? tiles.length - 7 : 0 });
              })}
            </div>
          )}
        </div>
      )}

      {/* Standard players, same rate + share experience as listing profiles. */}
      {videoIdx !== null && videos[videoIdx] && (
        <VideoPlayModal
          video={videos[videoIdx]}
          videos={videos}
          onSelect={(v) => { const ix = videos.findIndex((x) => x.id === v?.id); if (ix >= 0) setVideoIdx(ix); }}
          onClose={() => setVideoIdx(null)}
        />
      )}
      {photoIdx !== null && gallery[photoIdx] && (
        <PhotoLightbox
          gallery={gallery}
          idx={photoIdx}
          onClose={() => setPhotoIdx(null)}
          onPrev={() => setPhotoIdx((p) => (p - 1 + gallery.length) % gallery.length)}
          onNext={() => setPhotoIdx((p) => (p + 1) % gallery.length)}
          setLightIdx={setPhotoIdx}
        />
      )}

      {whyPhrases.length > 0 && (
        <Sect label={`Why Aura suggested ${name.split(" ")[0] || "them"}`} faint={faint}>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {whyPhrases.map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
                <span aria-hidden="true" style={{ color: GOLD, fontSize: 12, lineHeight: 1.35, flexShrink: 0 }}>✓</span>
                <span style={{ fontFamily: "var(--font-body)", fontSize: 12.5, color: body, lineHeight: 1.35 }}>{r}</span>
              </div>
            ))}
          </div>
        </Sect>
      )}

      {story && (
        <Sect label="Story" faint={faint}>
          <div style={{
            fontFamily: "var(--font-heading-primary)", fontSize: 13.5, color: body, lineHeight: 1.6,
            ...(storyOpen ? {} : { display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }),
          }}>
            {story}
          </div>
          {story.length > 180 && (
            <button
              onClick={() => setStoryOpen((v) => !v)}
              style={{ background: "none", border: "none", padding: "8px 0 0", cursor: "pointer", fontFamily: "var(--font-body)", fontSize: 12, color: GOLD, letterSpacing: "0.02em" }}
            >
              {storyOpen ? "Show less" : "Read more →"}
            </button>
          )}
        </Sect>
      )}

      {styles.length > 0 && (
        <Sect label="Style" faint={faint}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {styles.map((s, i) => (
              <span key={i} style={{ fontFamily: "var(--font-body)", fontSize: 11, color: body, border: "1px solid rgba(211,154,33,0.22)", borderRadius: 20, padding: "4px 11px", textTransform: "capitalize" }}>{s}</span>
            ))}
          </div>
        </Sect>
      )}

      {price && (
        <Sect label="From" faint={faint}>
          <div style={{ fontFamily: "var(--font-heading-primary)", fontSize: 19, color: text }}>{price}</div>
        </Sect>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
        {profileHref && <a href={profileHref} target="_blank" rel="noopener noreferrer" style={{ fontFamily: "var(--font-body)", fontSize: 12.5, color: GOLD, textDecoration: "none", borderBottom: "1px solid rgba(211,154,33,0.3)", alignSelf: "flex-start", paddingBottom: 1 }}>View full profile →</a>}
        {showcase?.slug && <a href={`/showcase/${showcase.slug}`} target="_blank" rel="noopener noreferrer" style={{ fontFamily: "var(--font-body)", fontSize: 12.5, color: faint, textDecoration: "none", alignSelf: "flex-start" }}>See their showcase →</a>}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function WorkspaceRight({ collapsed, darkMode }) {
  const recommendations        = useAura((s) => s.recommendations);
  const recommendationsLoading = useAura((s) => s.recommendationsLoading);
  const activeContext          = useAura((s) => s.activeContext);
  const articleRecommendations = useAura((s) => s.articleRecommendations);
  const articlesLoading        = useAura((s) => s.articlesLoading);
  const queryId                = useAura((s) => s.queryId);
  const sessionId              = useAura((s) => s.session.id);
  const conversationMode       = useAura((s) => s.conversationMode);
  const activeVenueId          = useAura((s) => s.activeVenueId);
  const auraShortlistedIds     = useAura((s) => s.auraShortlistedIds);
  const auraShortlistedItems   = useAura((s) => s.auraShortlistedItems);
  const enquiredVenueIds       = useAura((s) => s.enquiredVenueIds);
  const wedding                = useAura((s) => s.wedding);
  const weddingTeam            = useAura((s) => s.weddingTeam);
  const messages               = useAura((s) => s.messages);
  // Editorial / spotlights / real weddings for the wedding's contextual Explore
  // (these moved off the left "Discover", the left is the couple's own space).
  const discover = useMemo(() => {
    const lastAura = [...messages].reverse().find((m) => m.from === "aura");
    return { editorial: lastAura?.editorial_context || [], showcase: lastAura?.showcase_context || [], weddings: lastAura?.real_wedding_context || [] };
  }, [messages]);
  // Shop products from the last Aura turn. A separate channel from `recommendations`
  // by design: a purchasable product is not a member listing, and the rail must be
  // able to tell them apart (different card, different action, different source).
  const { shopProducts, shopBrowse } = useMemo(() => {
    const lastAura = [...messages].reverse().find((m) => m.from === "aura");
    return {
      shopProducts: Array.isArray(lastAura?.product_context) ? lastAura.product_context : [],
      shopBrowse: lastAura?.product_browse ?? null,
    };
  }, [messages]);
  const { items = [], summary = "Curated for you" } = recommendations ?? {};
  const hasLocationContext = !!(activeContext?.country || activeContext?.region);

  // Route every Aura result through the canonical transformer before rendering.
  // The Aura API can return snake_case fields (hero_images, price_from) and
  // divergent aliases; transformListing normalises everything to the platform
  // shape. Aura-specific fields (reason, scores, rank) are preserved as overrides
  // since they have no equivalent in the standard listing shape.
  const canonicalItems = useMemo(() =>
    items.map((item) => {
      const prepped = {
        ...item,
        // hero_images is the Aura API alias for imgs; collapse to the canonical name
        imgs:     (item.imgs?.length > 0) ? item.imgs : (item.hero_images ?? []),
      };
      const canonical = transformListing(prepped, {
        reason: item.reason  ?? null,
        scores: item.scores  ?? null,
        rank:   item.rank    ?? null,
      });
      assertCanonical(canonical, "WorkspaceRight/Aura");
      return canonical;
    }),
    [items]
  );
  const [qvItem, setQvItem] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [bulkEnquiryOpen, setBulkEnquiryOpen] = useState(false);
  // Single-venue enquiry: opened by "Send an enquiry" / "Ask about pricing" chips
  const [enquiryVenue, setEnquiryVenue] = useState(null);

  // Listen for enquiry open event fired by buying-signal chips in WorkspaceCenter
  useEffect(() => {
    const handler = () => {
      const target = items.find((i) => i.id === activeVenueId)
        ?? auraShortlistedItems[0]
        ?? null;
      // Do not reopen if this venue has already received an enquiry this session
      if (target && !enquiredVenueIds.includes(target.id)) {
        setEnquiryVenue(target);
      }
    };
    window.addEventListener('lwd:aura:open-enquiry', handler);
    return () => window.removeEventListener('lwd:aura:open-enquiry', handler);
  }, [items, activeVenueId, auraShortlistedItems, enquiredVenueIds]);
  const T = getT(darkMode);

  // Push Aura-shortlisted items into the site's existing compare strip
  const handleCompare = () => {
    // Map to the shape GlobalCompare expects and write to sessionStorage first.
    // GlobalCompare's event handler ignores detail, it calls loadCompareList()
    // from sessionStorage, so we must persist before dispatching the event.
    const mapped = auraShortlistedItems.map((item) => ({
      id:          item.id,
      name:        item.name,
      slug:        item.slug,
      city:        item.city,
      region:      item.region,
      country:     item.country,
      heroImage:   item.imgs?.[0] ?? null,
      // Pass full image array so compare gallery works without a fetch
      imgs:        item.imgs?.map?.((src) => ({ src, alt: item.name })) ?? [],
      capacity:    item.capacity,
      listingType: item.type,
      // Carry extra fields Aura already has, prevents the edge-fn hang
      priceFrom:      item.price_from ?? item.priceFrom ?? null,
      priceCurrency:  item.price_currency ?? item.priceCurrency ?? '€',
      shortDescription: item.short_description ?? item.shortDescription ?? null,
      verified:       item.verified ?? false,
      luxuryTier:     item.luxury_tier ?? item.luxuryTier ?? null,
      isAuraSource:   true, // flag so modal can skip fetch if needed
    }));
    saveCompareList(mapped);
    // Single event triggers GlobalCompare to re-read sessionStorage and show bar
    window.dispatchEvent(new CustomEvent('lwd:compare-bar', { detail: { source: 'aura' } }));
    // Open the compare modal directly, better UX than requiring a second click
    window.dispatchEvent(new CustomEvent('lwd:open-compare-modal'));
  };

  // Combine canonical venue/vendor items with article previews
  const allItems = [
    ...canonicalItems,
    ...articleRecommendations.map((article) => ({
      ...article,
      type: "article",
    })),
  ];

  const focusedEntity    = useAura((s) => s.focusedEntity);
  const clearEntityFocus = useAura((s) => s.clearEntityFocus);
  // Private Aura = a registered COUPLE with a wedding. Only they get the knowledge /
  // entity-focus column; everyone else (unregistered visitors AND companies) keeps
  // the original "Curated for you" recommendations. See project_two_aura_products.
  const { isAuthenticated } = useCoupleAuth();
  const isPrivate = isPreviewPrivate() || (isAuthenticated && !!wedding);

  if (collapsed) {
    return <CollapsedRight itemCount={items.length} darkMode={darkMode} />;
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: T.bg, boxSizing: "border-box", transition: "background 0.3s ease", position: "relative" }}>
      <style>{`@keyframes ws-shimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(100%)} }`}</style>

      {!isPrivate ? (
        /* ── PUBLIC / original ── "Curated for you" recommendations: the shared
           front door for unregistered visitors AND companies (venues/vendors). */
        <>
          <div style={{ flexShrink: 0, padding: "18px 18px 13px", borderBottom: `1px solid ${T.divider}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontFamily: "var(--font-heading-primary)", fontSize: 17, fontWeight: 500, color: T.text }}>
                Curated for you
              </div>
              {allItems.length > 0 && (
                <span style={{ background: "rgba(211,154,33,0.12)", border: `1px solid rgba(211,154,33,0.28)`, color: GOLD, fontSize: 10, fontWeight: 700, fontFamily: "var(--font-body)", padding: "2px 8px", borderRadius: 12 }}>
                  {allItems.length} result{allItems.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div style={{ fontFamily: "var(--font-body)", fontSize: 11, color: T.grey, opacity: 0.7, marginTop: 4 }}>{summary}</div>
            {conversationMode === 'enquiry_transition' && items.length > 0 && (
              <div style={{ marginTop: 8, padding: "5px 10px", borderRadius: 6, background: "rgba(211,154,33,0.08)", border: "1px solid rgba(211,154,33,0.2)", fontFamily: "var(--font-body)", fontSize: 10, color: GOLD, letterSpacing: "0.2px" }}>
                Ready to reach out? Use View or Enquire on your top match.
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "14px 14px 20px" }}>
            {recommendationsLoading && items.length === 0 ? (
              <><CardSkeleton darkMode={darkMode} /><CardSkeleton darkMode={darkMode} /><CardSkeleton darkMode={darkMode} /></>
            ) : allItems.length === 0 && shopProducts.length === 0 && !articlesLoading && hasLocationContext ? (
              <div style={{ textAlign: "center", padding: "52px 16px", fontFamily: "var(--font-body)", lineHeight: 1.65 }}>
                <div style={{ marginBottom: 14, opacity: 0.3 }}><Icon name="sparkle" size={26} color={GOLD} /></div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.text, opacity: 0.75, marginBottom: 8, fontFamily: "var(--font-heading-primary)" }}>No listings in this area yet.</div>
                <div style={{ fontSize: 12, color: T.grey, opacity: 0.6 }}>Try broadening to the full country, or ask Aura for nearby alternatives.</div>
              </div>
            ) : allItems.length === 0 && shopProducts.length === 0 && !articlesLoading ? (
              <div style={{ textAlign: "center", padding: "56px 12px", fontFamily: "var(--font-body)", fontSize: 13, color: T.grey, opacity: 0.5, lineHeight: 1.65 }}>
                <div style={{ marginBottom: 12, opacity: 0.4 }}><Icon name="sparkle" size={28} color={GOLD} /></div>
                Ask Aura about venues, regions, vendors, or editorial topics to see curated recommendations here.
              </div>
            ) : (
              <>
                {canonicalItems.map((item) => (
                  <RecommendationCard key={item.id} item={item} darkMode={darkMode} onQuickView={setQvItem} onViewFull={setQvItem} queryId={queryId} sessionId={sessionId} isActive={item.id === activeVenueId} isAuraShortlisted={auraShortlistedIds.includes(item.id)} isEnquired={enquiredVenueIds.includes(item.id)} />
                ))}
                {items.length > 0 && articleRecommendations.length > 0 && (
                  <div style={{ height: 1, background: T.divider, margin: "16px 0", opacity: 0.5 }} />
                )}
                {shopProducts.length > 0 && (
                  <>
                    {canonicalItems.length > 0 && (
                      <div style={{ height: 1, background: T.divider, margin: "16px 0", opacity: 0.5 }} />
                    )}
                    {shopProducts.map((p, i) => (
                      <ProductPreview key={p.url || i} product={p} darkMode={darkMode} />
                    ))}
                    {shopBrowse?.link && (
                      <a href={shopBrowse.link} target="_blank" rel="noopener noreferrer"
                         style={{ display: "block", textAlign: "center", padding: "8px 0 14px", fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: GOLD, textDecoration: "none" }}>
                        Browse all {shopBrowse.label}
                      </a>
                    )}
                  </>
                )}
                {articleRecommendations.map((article) => (
                  <div key={article.slug || article.id} style={{ marginBottom: 12 }}>
                    <ArticlePreview article={article} onSelect={(slug) => setSelectedArticle(article)} compact={false} />
                  </div>
                ))}
                {articlesLoading && (
                  <div style={{ textAlign: "center", padding: "20px 12px", fontFamily: "var(--font-body)", fontSize: 12, color: T.grey, opacity: 0.6 }}>
                    <div style={{ display: "flex", gap: 5, alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
                      {[0, 1, 2].map((i) => (<span key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: GOLD, animation: `ws-dot-pulse 1.4s ease ${i * 0.2}s infinite`, opacity: 0.6 }} />))}
                    </div>
                    Loading related articles…
                  </div>
                )}
              </>
            )}
          </div>
        </>
      ) : focusedEntity ? (
        /* ── PRIVATE ── the selected entity, recomposed into its dossier. */
        <EntityDossier entity={focusedEntity} darkMode={darkMode} onBack={clearEntityFocus} />
      ) : (
        /* ── PRIVATE ── the actual venue recommendations, browsable and actionable
           (shortlisting a card adds it to the wedding), with contextual editorial
           knowledge beneath. The couple should always be able to act on what Aura
           found, not just the single candidate on the canvas. */
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 16px 22px" }}>
          {canonicalItems.length > 0 && (
            <>
              <div style={{ fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD, marginBottom: 12 }}>
                For your wedding
              </div>
              {canonicalItems.map((item) => (
                <RecommendationCard key={item.id} item={item} darkMode={darkMode} onQuickView={setQvItem} onViewFull={setQvItem} queryId={queryId} sessionId={sessionId} isActive={item.id === activeVenueId} isAuraShortlisted={auraShortlistedIds.includes(item.id)} isEnquired={enquiredVenueIds.includes(item.id)} />
              ))}
            </>
          )}
          {(() => {
            const knowledge = [
              ...(discover.editorial || []).map((a) => ({ key: `e-${a.article_id}`, label: "Guide",        title: a.title, image: a.cover_image, href: `/lwd-edit/${a.slug}` })),
              ...(discover.weddings  || []).map((w) => ({ key: `w-${w.feature_id}`, label: "Real wedding", title: w.title, image: w.cover_image, href: `/real-weddings/${w.slug}` })),
              ...(discover.showcase  || []).map((s) => ({ key: `s-${s.showcase_id}`, label: s.badge_label || "Spotlight", title: s.title, image: s.cover_image, href: `/showcase/${s.slug}` })),
            ];
            if (!knowledge.length) return null;
            return (
              <>
                {canonicalItems.length > 0 && <div style={{ height: 1, background: T.divider, margin: "20px 0 16px", opacity: 0.5 }} />}
                {knowledge.map((k) => (
                  <ExploreRow key={k.key} label={k.label} title={k.title} image={k.image} href={k.href} darkMode={darkMode} />
                ))}
              </>
            );
          })()}
        </div>
      )}

      {/* ── Shortlist bar, appears when 2+ venues are Aura-shortlisted ──── */}
      {auraShortlistedItems.length >= 2 && (
        <ShortlistBar
          items={auraShortlistedItems}
          darkMode={darkMode}
          onEnquireAll={() => setBulkEnquiryOpen(true)}
          onCompare={handleCompare}
        />
      )}

      {/* Quick View modal */}
      {qvItem && (
        <QuickViewModal
          item={qvItem}
          onClose={() => setQvItem(null)}
          onViewFull={() => setQvItem(null)}
        />
      )}

      {/* Enquiry modal, single venue (buying-signal chips) or full shortlist */}
      {(bulkEnquiryOpen || enquiryVenue) && (
        <BulkEnquiryModal
          items={enquiryVenue ? [enquiryVenue] : auraShortlistedItems}
          onClose={() => { setBulkEnquiryOpen(false); setEnquiryVenue(null); }}
          darkMode={darkMode}
        />
      )}
    </div>
  );
}
